// @ts-nocheck
import React, { useState, useEffect } from 'react';
import * as api from '../api';
import type { OrgMember, Invite } from '../types';

interface Props {
  onClose: () => void;
}

export default function AdminPanel({ onClose }: Props) {
  const [tab, setTab] = useState<'members' | 'invites' | 'settings'>('members');
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteExternal, setInviteExternal] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const [lastInviteLink, setLastInviteLink] = useState('');

  useEffect(() => {
    Promise.all([api.getOrg(), api.getInvites()]).then(([orgRes, invRes]) => {
      setMembers(orgRes.members);
      setOrgName(orgRes.org?.name || '');
      setInvites(invRes.invites);
      setLoading(false);
    });
  }, []);

  const handleRoleChange = async (userId: string, role: string) => {
    await api.updateOrgMember(userId, { role });
    setMembers(members.map(m => m.user_id === userId ? { ...m, role: role as any } : m));
  };

  const handleToggleExternal = async (userId: string, current: number) => {
    const val = current ? 0 : 1;
    await api.updateOrgMember(userId, { can_share_external: val });
    setMembers(members.map(m => m.user_id === userId ? { ...m, can_share_external: val } : m));
  };

  const handleRemoveMember = async (userId: string) => {
    if (!confirm('Remove this member from the organization? They will lose access immediately.')) return;
    await api.removeOrgMember(userId);
    setMembers(members.filter(m => m.user_id !== userId));
  };

  const handleCreateInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviteMsg('');
    setLastInviteLink('');
    try {
      const result = await api.createInvite({
        email: inviteEmail.trim(),
        role: inviteRole,
        can_share_external: inviteExternal,
      });
      setInvites([result.invite, ...invites]);
      setLastInviteLink(result.invite_link);
      setInviteEmail('');
      setInviteMsg('Invite created! Copy the link below.');
    } catch (e: any) {
      setInviteMsg(e.message || 'Failed to create invite');
    }
  };

  const handleCopyLink = (token: string) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link);
    setInviteMsg('Link copied to clipboard!');
    setTimeout(() => setInviteMsg(''), 3000);
  };

  const handleDeleteInvite = async (id: string) => {
    await api.deleteInvite(id);
    setInvites(invites.filter(i => i.id !== id));
  };

  const handleSaveOrgName = async () => {
    if (!orgName.trim()) return;
    await api.updateOrg({ name: orgName.trim() });
    setInviteMsg('Organization name updated!');
    setTimeout(() => setInviteMsg(''), 3000);
  };

  const getInviteStatus = (invite: Invite) => {
    if (invite.accepted_at) return 'accepted';
    if (new Date(invite.expires_at) < new Date()) return 'expired';
    return 'pending';
  };

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ position: 'relative', maxWidth: 640 }}>
        <button className="modal-close" onClick={onClose}>&#10005;</button>
        <h2>Organization Admin</h2>

        <div className="admin-tabs">
          <button className={`admin-tab ${tab === 'members' ? 'active' : ''}`} onClick={() => setTab('members')}>
            Members ({members.length})
          </button>
          <button className={`admin-tab ${tab === 'invites' ? 'active' : ''}`} onClick={() => setTab('invites')}>
            Invites
          </button>
          <button className={`admin-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
            Settings
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <>
            {tab === 'members' && (
              <div>
                {members.map((m) => (
                  <div key={m.user_id} className="admin-member-row">
                    <div className="admin-member-info">
                      {m.avatar_url ? (
                        <img src={m.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%' }} />
                      ) : (
                        <div className="user-avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
                          {m.name?.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="admin-member-name">{m.name}</div>
                        <div className="admin-member-email">{m.email}</div>
                      </div>
                    </div>
                    <div className="admin-member-actions">
                      <select
                        value={m.role}
                        onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                      </select>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!m.can_share_external}
                          onChange={() => handleToggleExternal(m.user_id, m.can_share_external)}
                        />
                        External
                      </label>
                      <button
                        onClick={() => handleRemoveMember(m.user_id)}
                        style={{ color: '#e2445c', fontSize: 12, padding: '4px 8px' }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'invites' && (
              <div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Send New Invite</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@example.com"
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateInvite()}
                      style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #e6e9ef', borderRadius: 6, outline: 'none', fontSize: 13 }}
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      style={{ padding: '8px', borderRadius: 6, border: '1px solid #e6e9ef', fontSize: 13 }}
                    >
                      <option value="member">Member</option>
                      <option value="admin">Admin</option>
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={inviteExternal}
                        onChange={(e) => setInviteExternal(e.target.checked)}
                      />
                      Can share externally
                    </label>
                    <button
                      onClick={handleCreateInvite}
                      style={{ padding: '8px 16px', background: '#6161ff', color: 'white', borderRadius: 6, fontSize: 13, fontWeight: 500 }}
                    >
                      Invite
                    </button>
                  </div>
                  {inviteMsg && (
                    <div style={{
                      fontSize: 13, marginTop: 8,
                      color: inviteMsg.includes('Failed') || inviteMsg.includes('already') ? '#e2445c' : '#00c875',
                    }}>
                      {inviteMsg}
                    </div>
                  )}
                  {lastInviteLink && (
                    <div style={{
                      marginTop: 8, padding: '8px 12px', background: '#f6f7fb', borderRadius: 6,
                      fontSize: 12, wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <span style={{ flex: 1 }}>{lastInviteLink}</span>
                      <button
                        className="copy-link-btn"
                        onClick={() => { navigator.clipboard.writeText(lastInviteLink); setInviteMsg('Copied!'); }}
                      >
                        Copy
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>All Invites</div>
                {invites.length === 0 ? (
                  <div style={{ color: '#676879', fontSize: 13 }}>No invites yet.</div>
                ) : (
                  invites.map((inv) => {
                    const status = getInviteStatus(inv);
                    return (
                      <div key={inv.id} className="admin-invite-row">
                        <div>
                          <div style={{ fontSize: 14 }}>{inv.email}</div>
                          <div style={{ fontSize: 12, color: '#676879' }}>
                            Role: {inv.role} | Expires: {new Date(inv.expires_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className={`invite-status ${status}`}>
                            {status}
                          </span>
                          {status === 'pending' && (
                            <>
                              <button className="copy-link-btn" onClick={() => handleCopyLink(inv.token)}>
                                Copy Link
                              </button>
                              <button
                                onClick={() => handleDeleteInvite(inv.id)}
                                style={{ color: '#e2445c', fontSize: 12, padding: '4px 8px' }}
                              >
                                Revoke
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {tab === 'settings' && (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                  Organization Name
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid #e6e9ef', borderRadius: 8, fontSize: 14, outline: 'none' }}
                  />
                  <button
                    onClick={handleSaveOrgName}
                    style={{ padding: '10px 20px', background: '#6161ff', color: 'white', borderRadius: 8, fontSize: 14, fontWeight: 500 }}
                  >
                    Save
                  </button>
                </div>
                {inviteMsg && (
                  <div style={{ fontSize: 13, marginTop: 8, color: '#00c875' }}>{inviteMsg}</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
